import { LinkPromoPreviewProvider } from "../state/link-promo-preview";
import { useEffect, useMemo, useState } from "react";
import type {
  EmbedAttributes,
  WidgetBrand,
  WidgetConfigPayload,
  WidgetService,
} from "../types";
import { loadWidgetConfig } from "../config";
import { initTracking } from "../state/tracking";
import { ctaColorVars, resolveTheme, themeDataAttrs, themeToCssVars } from "../theme";
import { CartProvider } from "../state/cart";
import { GuestProvider } from "../state/guest";
import { GuestIdentityGate } from "./GuestIdentityGate";
import { WidgetShell } from "./WidgetShell";
import { WidgetLayerScopeProvider } from "./WidgetLayer";
import { optionDeepLinkTarget } from "../state/landing";
import {
  resolveWidgetLayout,
  resolveWidgetLayouts,
  WIDGET_LAYOUT_MOBILE_BREAKPOINT,
} from "@vertu/shared";

export function WidgetApp({ embed }: { embed: EmbedAttributes }) {
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "error"; message: string }
    | { status: "ready"; payload: WidgetConfigPayload }
  >({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    loadWidgetConfig(embed.tenantSlug, embed.widgetSlug).then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setState({ status: "ready", payload: result.payload });
        return;
      }
      const messages = {
        not_found: `Widget "${embed.widgetSlug}" not found for "${embed.tenantSlug}".`,
        config_error: result.detail ?? "Widget is misconfigured.",
        network_error: `Couldn't reach the booking service${result.detail ? `: ${result.detail}` : "."}`,
      };
      setState({ status: "error", message: messages[result.reason] });
    });
    return () => {
      cancelled = true;
    };
  }, [embed.tenantSlug, embed.widgetSlug]);

  if (state.status === "loading") return <LoadingSkeleton />;
  if (state.status === "error") return <ErrorCard message={state.message} />;

  return <WidgetReady embed={embed} payload={state.payload} />;
}

// How many cards per row at each breakpoint. Defaults lean wide on big screens
// (4-up) and collapse gracefully to 1-up on a phone; every value is clamped to
// 1–6. Overridden per widget via settings.card_columns from the builder.
export const CARD_COLUMN_DEFAULTS = {
  mobile_portrait: 1,
  mobile_landscape: 2,
  tablet_portrait: 2,
  tablet_landscape: 3,
  laptop: 3,
  wide: 4,
} as const;

export type CardColumnKey = keyof typeof CARD_COLUMN_DEFAULTS;

function cardColumnCssVars(raw: unknown): Record<string, string> {
  const cfg = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;
  const pick = (key: CardColumnKey): number => {
    const v = Number(cfg[key]);
    if (Number.isFinite(v) && v >= 1 && v <= 6) return Math.floor(v);
    return CARD_COLUMN_DEFAULTS[key];
  };
  return {
    "--vt-cols-mp": String(pick("mobile_portrait")),
    "--vt-cols-ml": String(pick("mobile_landscape")),
    "--vt-cols-tp": String(pick("tablet_portrait")),
    "--vt-cols-tl": String(pick("tablet_landscape")),
    "--vt-cols-laptop": String(pick("laptop")),
    "--vt-cols-wide": String(pick("wide")),
  };
}

function WidgetReady({
  embed,
  payload,
}: {
  embed: EmbedAttributes;
  payload: WidgetConfigPayload;
}) {
  const { tenant, widget, brands, services } = payload;

  // Load the tenant's ad/analytics tags once config is ready (consent-gated
  // inside the tracking module).
  useEffect(() => {
    initTracking(payload.tracking ?? null);
  }, [payload.tracking]);

  // Brand scope: embed attribute wins over widget config
  const embedBrand = embed.brandSlug
    ? brands.find((b) => b.slug === embed.brandSlug)
    : null;
  const widgetBrand = widget.brand_id
    ? brands.find((b) => b.id === widget.brand_id)
    : null;
  const activeBrand: WidgetBrand | null = embedBrand ?? widgetBrand ?? null;

  // Filter services by the enabled list and brand scope
  const enabled = new Set(widget.enabled_service_ids ?? []);
  const visibleServices: WidgetService[] = services
    .filter((s) => enabled.size === 0 || enabled.has(s.id))
    .filter((s) => !activeBrand || s.brand_id === activeBrand.id)
    .sort((a, b) => {
      if (enabled.size > 0) {
        const idsArr = widget.enabled_service_ids ?? [];
        return idsArr.indexOf(a.id) - idsArr.indexOf(b.id);
      }
      return (a.display_order ?? 0) - (b.display_order ?? 0);
    });

  // A ?option= link (data-option) opens its service with that option chosen
  // (state/landing.ts), so a venue's "Book this ride" button needs no ?tab=.
  const optionTarget = useMemo(
    () => optionDeepLinkTarget(embed.initialOptionSlug, embed.defaultTabSlug, visibleServices),
    [embed.initialOptionSlug, embed.defaultTabSlug, visibleServices],
  );

  const defaultService = useMemo(() => {
    // Only a SERVICE slug can pick a default service. A pseudo-tab id
    // (__events__ / __functions__ / __enquiries__ / __packages__) matches no
    // service, so it used to fall silently through to the widget default —
    // which is why ?tab=__events__ opened the first service instead. Pseudo
    // tabs are passed to WidgetShell as embedDefaultTab and resolved there.
    if (embed.defaultTabSlug && !embed.defaultTabSlug.startsWith("__")) {
      const match = visibleServices.find((s) => s.slug === embed.defaultTabSlug);
      if (match) return match;
    }
    if (optionTarget) {
      const match = visibleServices.find((s) => s.id === optionTarget.serviceId);
      if (match) return match;
    }
    if (widget.default_service_id) {
      const match = visibleServices.find(
        (s) => s.id === widget.default_service_id,
      );
      if (match) return match;
    }
    return visibleServices[0] ?? null;
  }, [embed.defaultTabSlug, optionTarget, widget.default_service_id, visibleServices]);

  const theme = useMemo(
    () => resolveTheme(tenant, widget, activeBrand),
    [tenant, widget, activeBrand],
  );

  const cssVars = { ...themeToCssVars(theme), ...ctaColorVars(widget) };
  // Per-breakpoint card columns (how many service/room/option cards per row),
  // configurable in the widget builder. Emitted as CSS custom properties the
  // .vt-option-cards / panel grids consume at each media breakpoint.
  const columnVars = cardColumnCssVars(widget.settings?.card_columns);

  if (visibleServices.length === 0) {
    return <ErrorCard message="No services are available to book right now." />;
  }

  // Every upcoming published event shows. The widget's featured list
  // (enabled_event_ids) curates ORDER — featured lead, in list order,
  // series children inheriting their parent's slot — it never hides the
  // rest. The v29 RPC already returns that ordering; sorting again here
  // keeps older cached payloads consistent and costs nothing.
  const allEvents = payload.events ?? [];
  const curatedEventIds = widget.enabled_event_ids ?? [];
  const featuredRank = (e: (typeof allEvents)[number]): number => {
    const own = curatedEventIds.indexOf(e.id);
    if (own !== -1) return own;
    const parent = e.series_parent_id ? curatedEventIds.indexOf(e.series_parent_id) : -1;
    return parent !== -1 ? parent : Number.MAX_SAFE_INTEGER;
  };
  const visibleEvents = [...allEvents].sort(
    (a, b) =>
      featuredRank(a) - featuredRank(b) ||
      a.starts_at.localeCompare(b.starts_at),
  );
  const showEventsTab =
    Boolean(widget.show_events_tab) && visibleEvents.length > 0;
  // Per-breakpoint layout: pick desktop/mobile by viewport width, unless the
  // preview forces a device via the embed (data-vt-device). data-vt-layout
  // drives the per-layout CSS and swaps live as the window crosses the
  // breakpoint. Stored in widget settings.layout as { desktop, mobile }
  // (a bare string is treated as both, for back-compat).
  const layouts = resolveWidgetLayouts(widget.settings?.layout);
  const [viewportMobile, setViewportMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.innerWidth < WIDGET_LAYOUT_MOBILE_BREAKPOINT,
  );
  useEffect(() => {
    if (embed.device) return; // forced device (preview) — don't track resize
    const onResize = () =>
      setViewportMobile(window.innerWidth < WIDGET_LAYOUT_MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [embed.device]);
  const breakpoint = embed.device ?? (viewportMobile ? "mobile" : "desktop");
  // The admin preview can force a layout (live, before save); otherwise the
  // layout comes from the per-breakpoint saved settings.
  const layout = embed.forcedLayout
    ? resolveWidgetLayout(embed.forcedLayout)
    : layouts[breakpoint];

  // The root's look, which the overlays' layers on <body> repeat
  // (WidgetLayer.tsx) — they are outside this <div>, so inherit nothing from it.
  const rootStyle = { ...cssVars, ...columnVars } as React.CSSProperties;
  const rootAttrs: Record<string, string> = {
    "data-brand": activeBrand?.slug ?? "",
    "data-vt-layout": layout,
    ...themeDataAttrs(widget),
  };

  return (
    <div className="vt-widget" style={rootStyle} {...rootAttrs}>
      <WidgetLayerScopeProvider value={{ style: rootStyle, attrs: rootAttrs }}>
        <CartProvider currency={tenant.currency ?? "ZAR"}>
          <LinkPromoPreviewProvider tenant={tenant}>
          <GuestProvider tenantId={tenant.id}>
            <GuestIdentityGate />
            <WidgetShell
            tenant={tenant}
            widget={widget}
            layout={layout}
            activeBrand={activeBrand}
            brands={brands}
            services={visibleServices}
            defaultService={defaultService}
            panels={payload.panels ?? []}
            events={visibleEvents}
            showEventsTab={showEventsTab}
            embedDefaultTab={embed.defaultTabSlug || optionTarget?.serviceSlug}
            initialOption={optionTarget}
            initialEventSlug={embed.initialEventSlug}
            initialComboSlug={embed.initialComboSlug}
            functions={payload.functions ?? null}
            tenantSlug={embed.tenantSlug}
            widgetSlug={embed.widgetSlug}
              aiAssistant={payload.ai_assistant ?? null}
            />
          </GuestProvider>
          </LinkPromoPreviewProvider>
        </CartProvider>
      </WidgetLayerScopeProvider>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="vt-widget vt-loading">
      <div className="vt-loading-bar" />
      <span>Loading booking options…</span>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="vt-widget vt-error">
      <strong>Widget unavailable.</strong>
      <p>{message}</p>
      <p className="vt-muted" style={{ fontSize: 12, marginTop: 12 }}>
        Checklist:
        <br />• Tenant exists with a matching slug
        <br />• Widget with that slug exists &amp; is active
        <br />• Migrations 001–009 pushed to Supabase
        <br />• At least one service is enabled for the tenant
      </p>
    </div>
  );
}
